// TODO: rename to StudyDataLocationsGrid
// Used on the Study page's Study Data Locations section
import { useState, useEffect } from "react"
import { useShallow } from "zustand/react/shallow"
import GridTypeA from "components/grids/GridTypeA/GridTypeA"
import { isObject } from "lodash"

// Cell Renderers
import SiteStatusCellRenderer from "../../../Locations/SiteDetails/CellRenderers/SiteStatusCellRenderer"
import PRSValidation from "components/ag-grid-cell-renderers/LocationsPRSValidation/PRSValidation"

// context
import useAuthoringValidationsStore from "../../hooks/stores/useAuthoringValidationsStore"

// utils
import { formatContact } from "containers/studies/Study/subcomponents/Authoring/subcomponents/SitesGrid/contactsUtils"

const SitesGrid = (props) => {
  const { sites, study } = props
  const [rowData, setRowData] = useState([])

  const { sitesValidations } = useAuthoringValidationsStore(
    useShallow((state) => ({
      sitesValidations: state.sitesValidations,
    }))
  )

  useEffect(() => {
    if (sites) {
      formatSites()
    }
  }, [sites, sitesValidations])

  const formatSites = () => {
    let formattedSites = []

    if (sites && Array.isArray(sites)) {
      sites?.forEach((item) => {
        let formattedSite = {
          ...item,
        }

        let maskedInformation = {}
        if (formattedSite?.location_data?.mask) maskedInformation = formattedSite.location_data.mask
        formattedSite.location_data.source = {
          ...formattedSite.location_data.ctg,
          ...formattedSite.location_data.source,
          ...maskedInformation,
        }

        formattedSites.push(formattedSite)
      })
    }

    if (sitesValidations?.length > 0) {
      let valMap = {}

      sitesValidations.forEach((validation) => {
        if (valMap[validation.site_db_id]) {
          valMap[validation.site_db_id].push(validation)
        } else {
          valMap[validation.site_db_id] = [validation]
        }
      })

      formattedSites = formattedSites.map((site) => {
        return {
          ...site,
          validations: valMap[site.id] ? valMap[site.id] : [],
        }
      })
    }
    setRowData(formattedSites)
  }

  const formatInvestigators = (arr, type) => {
    const strArr = []
    arr
      .filter((person) => person.role === type)
      .forEach((person) => {
        const str = formatContact(person)
        strArr.push(str)
      })

    return strArr.join("\n")
  }

  const gridOptions = {
    columnDefs: [
      {
        headerValueGetter: ({ location }) => {
          switch (location) {
            case "columnToolPanel":
              return "Site Status"
            default: // case "header"
              return ""
          }
        },
        colId: "Site Status",
        cellRenderer: SiteStatusCellRenderer,
        cellClass: "simpleField",
        hide: true,
        comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
          if (nodeA?.data?.only_on_ctg) return 1
          else if (nodeA?.data?.only_on_source) return -1
          return 0
        },
        valueSetter: (params) => {
          let res = []
          if (params.data) {
            if (params.data.is_new) res.push("New")
            if (params.data.only_on_source) res.push("No CTG")
            if (params.data.only_on_ctg) res.push("No source")
            if (params.data.recruitment_status_changed) res.push("Recruitment status changed")
            return res.join(", ")
          }
          return null
        },
        getValue: (params) => {
          let res = []
          if (params.data) {
            if (params.data.is_new) res.push("New")
            if (params.data.only_on_source) res.push("No CTG")
            if (params.data.only_on_ctg) res.push("No source")
            if (params.data.recruitment_status_changed) res.push("Recruitment status changed")
            return res.join(", ")
          }
          return null
        },
      },
      {
        headerName: "Site ID",
        field: "site_rowid",
        cellClass: "simpleField",
        hide: true,
        valueGetter: (params) => {
          if (params?.data?.location_data?.source?.site_rowid) {
            return params.data.location_data.source.site_rowid
          } else if (params?.data?.site_id) {
            return params.data.site_id
          }
          return ""
        },
      },
      {
        headerName: "Site Number",
        field: "location_data.source.site_number",
        cellClass: "simpleField",
        hide: true,
      },
      {
        headerName: "Site Activation Date",
        field: "location_data.source.site_active_date",
        cellClass: "simpleField",
        hide: true,
      },
      {
        headerName: "Recruitment Status",
        field: "location_data.source.status",
      },
      {
        headerName: "PRS Validation",
        field: "validations",
        getValue: (params) => {
          if (params?.data?.validations) {
            let validations = params?.data?.validations
            if (isObject(validations) && !Array.isArray(validations) && validations !== null) {
              validations = [validations]
            }

            let errors = []
            let nonSiteErrors = []
            let warnings = []
            let notes = []

            if (validations && validations.length > 0) {
              validations.forEach((item) => {
                if (item.severity === "ERROR") {
                  if (item.site_db_id) {
                    errors.push(item)
                  } else {
                    nonSiteErrors.push(item)
                  }
                } else if (item.severity === "WARNING") warnings.push(item)
                else notes.push(item)
              })

              let items = []
              if (errors.length > 0) items.push(errors.length + " error(s)")
              if (warnings.length > 0) items.push(warnings.length + " warning(s)")
              if (notes.length > 0) items.push(notes.length + " note(s)")
              if (nonSiteErrors.length > 0) items.push(nonSiteErrors.length + " non-site errors")

              if (items && items.length > 0) return items.join(", ")

              return null
            }
          }
          return null
        },
        cellRenderer: PRSValidation,
        cellRendererParams: {
          source: "study-details",
          onOpenModal: () => {},
          studyData: study,
        },
        filter: false,
      },
      {
        headerName: "Facility Name",
        field: "location_data.source.facility.name",
      },
      {
        headerName: "City",
        field: "location_data.source.facility.address.city",
      },
      {
        headerName: "State/Province",
        field: "location_data.source.facility.address.state",
      },
      {
        headerName: "Country",
        field: "location_data.source.facility.address.country",
      },
      {
        headerName: "Postal Code",
        field: "location_data.source.facility.address.zip",
      },
      {
        headerName: "Site Contact",
        field: "location_data.source.contact",
        valueGetter: ({ data }) => {
          return formatContact(data?.location_data.source.contact)
        },
      },
      {
        headerName: "Backup Site Contact",
        field: "location_data.source.contact_backup",
        valueGetter: ({ data }) => {
          return formatContact(data?.location_data.source.contact_backup)
        },
      },
      {
        headerName: "Principal Investigator",
        field: "location_data.source.investigator",
        valueGetter: ({ data }) => {
          if (!data?.location_data?.source?.investigator || !Array.isArray(data?.location_data?.source?.investigator))
            return null

          return formatInvestigators(data?.location_data.source.investigator, "Principal Investigator")
        },
      },
      {
        headerName: "Sub-Investigator",
        field: "location_data.source.investigator",
        valueGetter: ({ data }) => {
          if (!data?.location_data?.source?.investigator || !Array.isArray(data?.location_data?.source?.investigator))
            return null
          return formatInvestigators(data?.location_data.source.investigator, "Sub-Investigator")
        },
      },
    ],
    defaultColDef: {
      wrapText: true,
      filter: true,
      enablePivot: true,
      enableCharts: false,
      enableRowGroup: false,
      filterParams: { clearButton: true },
      cellStyle: { whiteSpace: "normal !important" },
      autoHeight: true,
      cellClassRules: {
        oddBackground: function (params) {
          return params.rowIndex % 2 == 0
        },
      },
    },
  }

  return (
    <GridTypeA
      gridOptions={gridOptions}
      rowData={rowData}
      domLayout="autoHeight"
      pagination={true}
      paginationPageSize={10}
      paginationPageSizeSelector={[10, 20, 50, 100]}
    />
  )
}

export default SitesGrid
